// Display metadata for retrieval strategies, agent tools and routing modes,
// shared by the retrieval card (deep-dive) and the step timeline (live view).

export const STRATEGY_TOOLTIPS: Record<string, string> = {
  vector:                'Semantic search — finds passages by meaning similarity',
  bm25:                  'Keyword search — exact term matching for maximum recall',
  entity_lookup:         'Looks up all text chunks mentioning a specific named entity',
  temporal:              'Semantic search filtered and sorted by document date',
  contextual:            'Semantic search with keyword score boosting',
  hyde:                  'Hypothetical Document Embeddings — generates an imagined answer, then searches for real matches',
  summary:               'Searches document summaries first, then retrieves chunks from the best-matching reports',
  graph_path:            'Finds text chunks that co-mention two entities (graph traversal)',
  attribution:           'Finds which threat actors use a malware family (graph relationship)',
  ioc_pivot:             'Pivots from an indicator (IP, hash, domain) to related threats and entities',
  ioc_correlation:       'Finds entities that share indicators with a given entity',
  detection:             'Matches a list of indicators against the knowledge base to identify threats',
  comprehensive_profile: 'Full entity profile — combines attribution, IOCs, timeline, and semantic search',
  web_search:            'Live internet search for recent or emerging threats not yet in the local corpus',
  code:                  'Searches code blocks — commands, scripts, registry keys',
  trend_summary:         'Aggregate activity digest for a date window — most-mentioned entities and changes',
  // Agent tools (hybrid retrieval). The model picks these itself, in order.
  search:                'Tool: searches the report corpus (semantic, keyword, summary or code) and returns document cards',
  entity:                'Tool: knowledge-graph view of a threat actor or malware family — profile, documents or relations',
  ioc:                   'Tool: looks up indicators (IP, domain, URL, hash, CVE) and the threats they identify',
  path:                  'Tool: how two entities are connected in the knowledge graph, plus reports mentioning both',
  get_passages:          'Tool: reads the full text of specific passages of one report',
  trends:                'Tool: aggregate activity digest for a date window',
};

// Strategy → family grouping. Families are colored by hue (CSS
// `--malpedia-strat-*` vars / `data-strategy` rules) so a split reads as
// semantic vs lexical vs graph vs IOC vs web.
export type StrategyFamily = 'semantic' | 'lexical' | 'graph' | 'ioc' | 'web' | 'read' | 'trend';

const STRATEGY_FAMILY: Record<string, StrategyFamily> = {
  vector: 'semantic', hyde: 'semantic', contextual: 'semantic', summary: 'semantic', temporal: 'semantic',
  search: 'semantic',
  bm25: 'lexical', code: 'lexical',
  graph_path: 'graph', attribution: 'graph', entity_lookup: 'graph', comprehensive_profile: 'graph',
  entity: 'graph', ioc: 'graph', path: 'graph',
  ioc_pivot: 'ioc', ioc_correlation: 'ioc', detection: 'ioc',
  web_search: 'web',
  get_passages: 'read',
  trend_summary: 'trend', trends: 'trend',
};

export const familyOf = (name: string): StrategyFamily => STRATEGY_FAMILY[name] ?? 'semantic';

// Prettified strategy names (e.g. `ioc_pivot` → "IOC pivot").
const STRATEGY_DISPLAY: Record<string, string> = {
  vector: 'Vector', bm25: 'BM25', hyde: 'HyDE', contextual: 'Contextual',
  summary: 'Summary', temporal: 'Temporal', entity_lookup: 'Entity lookup',
  graph_path: 'Graph', attribution: 'Attribution', comprehensive_profile: 'Full profile',
  ioc_pivot: 'IOC pivot', ioc_correlation: 'IOC correlation', detection: 'Detection',
  web_search: 'Web search', code: 'Code', trend_summary: 'Trends',
  search: 'Search', entity: 'Entity', ioc: 'IOC', path: 'Path',
  get_passages: 'Read passages', trends: 'Trends',
};

export const displayName = (name: string): string =>
  STRATEGY_DISPLAY[name] ?? name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Retired modes (local/global/drift) stay here so persisted chats from before
// the hybrid-retrieval switch still render their routing badge.
export const MODE_TOOLTIPS: Record<string, string> = {
  auto: 'Analyzes your question and picks the best approach',
  vector: 'Finds passages by meaning similarity and keywords',
  local: 'Follows entity relationships from initial text matches to find connected intelligence',
  global: 'Looks up threat actors and malware families by name',
  drift: 'Explores outward from matches — extracts entities, then re-searches to find unexpected connections',
  planned: 'AI planner combines multiple search approaches for complex questions',
  llm_only: 'No document search — answers from conversation only',
};

export const MODE_LABELS: Record<string, string> = {
  auto: 'Auto',
  vector: 'Semantic',
  local: 'Connected',
  global: 'Entity',
  drift: 'Discovery',
  planned: 'Planned',
  llm_only: 'Chat only',
};

/** One-line reading of a tool call's arguments, e.g. `APT28 · relations`. */
export function toolArgSummary(tool: string, args: Record<string, unknown> | undefined): string {
  const a = args ?? {};
  const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v));
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(str) : v == null ? [] : [str(v)]);
  const parts: string[] = [];
  switch (tool) {
    case 'search':
      parts.push(list(a.queries).map((q) => `“${q}”`).join(' '));
      if (a.method) parts.push(str(a.method));
      break;
    case 'entity':
      parts.push(str(a.name));
      if (a.view) parts.push(str(a.view));
      break;
    case 'ioc':
      parts.push(list(a.values).join(', '));
      break;
    case 'path':
      parts.push(`${str(a.a)} ↔ ${str(a.b)}`);
      break;
    case 'get_passages':
      parts.push(str(a.cite_key));
      if (Array.isArray(a.chunk_indices)) parts.push(`chunks ${a.chunk_indices.join(', ')}`);
      break;
    case 'trends':
      break;
    default:
      return Object.entries(a).map(([k, v]) => `${k}=${str(v)}`).join(', ');
  }
  if (a.date_from || a.date_to) parts.push(`${str(a.date_from) || '…'} → ${str(a.date_to) || '…'}`);
  return parts.filter(Boolean).join(' · ');
}

/** Short target of a planned strategy, e.g. the entity or indicator it is about. */
export function strategyTarget(params: Record<string, unknown> | undefined): string {
  if (!params) return '';
  const a = params.entity_a;
  const b = params.entity_b;
  if (typeof a === 'string' && typeof b === 'string') return `${a} ↔ ${b}`;
  for (const k of ['entity_name', 'malware_name', 'ioc_value']) {
    const v = params[k];
    if (typeof v === 'string' && v) return v;
  }
  if (Array.isArray(params.ioc_values) && params.ioc_values.length > 0) {
    const n = params.ioc_values.length;
    return n === 1 ? String(params.ioc_values[0]) : `${n} indicators`;
  }
  return '';
}
